import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HomeIcon from '@mui/icons-material/Home'
import {
  Alert,
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography
} from '@mui/material'
import React, { FC, ReactElement, useContext, useEffect } from 'react'
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom'

import { AppRoutes } from '../../models'
import { NotificationContext } from '../../providers'
import { CenteredLoading } from '../CenteredLoading/CenteredLoading'
import { NotificationBadge } from '../NotificationBadge/NotificationBadge'

interface Props {
  title: string
  actionsRight?: ReactElement
  actionsLeft?: ReactElement
  children?: React.ReactNode
  isLoading?: boolean
  hideBottomBar?: boolean
  noXpadding?: boolean
  isAuthPage?: boolean
}

const DviPage: FC<Props> = ({
  title,
  children,
  actionsRight,
  actionsLeft,
  isLoading,
  hideBottomBar,
  noXpadding,
  isAuthPage
}) => {
  const { pathname } = useLocation()
  useEffect(() => {
    localStorage.setItem('appPreference', 'dvi')
  }, [])
  useEffect(() => {
    document.title = `${title} - InTune DVI`
  }, [title])
  const getNavValue = () => {
    if (matchPath(`${AppRoutes.DVI_INSPECTIONS}/*`, pathname)) {
      return 'Inspections'
    }
    if (matchPath(`${AppRoutes.DVI_JOBS}/*`, pathname)) {
      return 'Jobs'
    }
    if (matchPath(AppRoutes.DVI_HOME, pathname)) {
      return 'Home'
    }
    if (matchPath(AppRoutes.DVI_PROFILE, pathname)) {
      return 'Profile'
    }
    return ''
  }

  const { notice, setNotice, error, setError } = useContext(NotificationContext)
  return (
    <Box sx={{ pb: 7, pt: 7 }}>
      {isLoading ? (
        <CenteredLoading />
      ) : (
        <>
          <AppBar>
            <Grid
              direction="row"
              container
              alignItems="center"
              sx={{
                height: '3.5rem'
              }}>
              <Grid item xs={3}>
                <Box>{actionsRight}</Box>
              </Grid>
              <Grid item xs={6}>
                <Typography component="h1" variant="h3" textAlign="center">
                  {title}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Box>{actionsLeft}</Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    sx={{ color: 'white' }}
                    onClick={() =>
                      window.open(AppRoutes.HELP_WEBSITE, '_blank')
                    }>
                    <HelpOutlineIcon />
                  </IconButton>
                  {!isAuthPage && <NotificationBadge isDvi />}
                </Box>
              </Grid>
            </Grid>
          </AppBar>
          <Stack
            sx={{
              py: 1,
              px: noXpadding ? 0 : 1,
              boxSizing: 'border-box'
            }}
            spacing={1}>
            {children}
          </Stack>
        </>
      )}
      {!hideBottomBar && (
        <>
          <Paper
            z-index={1000}
            elevation={4}
            sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
            <Divider />
            <BottomNavigation showLabels value={getNavValue()}>
              <BottomNavigationAction
                component={RouterLink}
                value="Home"
                to={AppRoutes.DVI_HOME}
                label="Home"
                icon={<HomeIcon />}
              />
              <BottomNavigationAction
                component={RouterLink}
                value="Jobs"
                to={AppRoutes.DVI_JOBS}
                label="Jobs"
                icon={<AssignmentIcon />}
              />
              <BottomNavigationAction
                component={RouterLink}
                value="Inspections"
                to={AppRoutes.DVI_INSPECTIONS}
                label="Inspections"
                icon={<BuildCircleIcon />}
              />
              <BottomNavigationAction
                component={RouterLink}
                value="Profile"
                to={AppRoutes.DVI_PROFILE}
                label="Profile"
                icon={<AccountCircleIcon />}
              />
            </BottomNavigation>
          </Paper>
        </>
      )}
      <Snackbar
        open={!!notice}
        autoHideDuration={5000}
        onClose={() => {
          setNotice('')
        }}>
        <Alert severity="success" variant="filled">
          {notice}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => {
          setError('')
        }}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DviPage
